// Considered: SpaceRendererOrganism — rejected because it mounts a whole `SpaceDocument`, not a
// composer-assembled `SpaceHostMessage[]` (draft + override buffer + ghost buffer). This wraps
// `CompositionHost` directly, per the plan's ratified tiering (organism, since it imports
// `SpaceRendererOrganism`'s sibling `CompositionHost` — molecule-imports-organism-tier primitives
// is the same Rule 2 concern the sphere picker tiering fix already established). It also forwards
// `catalogEntries` (plan seam 12 — `SpaceRendererOrganism` pins its own filtered list and doesn't
// expose the prop) so `inspect`/selection resolve a `catalogEntry`, which Try mode's write-shape
// classification depends on (`isWriteShapedAction`).
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import { useTranslation } from 'localization';
import CompositionHost, { type CompositionHostHandle } from '../../../composition-host/CompositionHost';
import { useCompositionResolver } from '../../../composition-host/useCompositionResolver';
import { assembleRenderInput, type ComposerState } from '../../../composer/composerStore';
import type { GridPosition } from '../../../composer/gridPlacement';
import { computeWireOverlayLines, type WireOverlayLine } from '../../../composer/wireOverlayGeometry';
import { SPACE_CATALOG_ENTRIES } from '../../../composer/actionClassification';
import type { SelectedNode, SpaceAction } from '../../../composition-host/types';
import { type ToolbarItem } from '../../organisms/metaspace-toolbar/metaspacetoolbar';
import { shouldShowMetaspaceToolbar } from '@center-platform/space-format/legacy';
import LoaderMolecule from '../../molecules/Loader/LoaderMolecule';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import './ComposerCanvasOrganism.scss';

export type ComposerCanvasViewMode = 'compose' | 'try' | 'wires';

export interface ComposerCanvasOrganismProps {
  state: ComposerState;
  /** Absent when not yet loaded/authenticated — the canvas renders nothing (never a live client). */
  apiClient?: ApiClient;
  /** The composer-local anchor sphere (never the global `selectSphereId` — SC4). */
  sphereId?: string;
  onSelect?: (node: SelectedNode | null) => void;
  /** Resolves and selects this component id once, on the first render it becomes resolvable (EAINT-865 gap 14 — `?node=` deep links). */
  initialSelectedNodeId?: string;
  /** Routes fired actions to Try mode's action log + wire delivery. Absent in `compose`/`wires` mode. */
  onAction?: (action: SpaceAction) => void;
  /** `try` wires up `onAction`; `wires` overlays the draft's declared wires as SVG lines (SC5, SC6). */
  viewMode?: ComposerCanvasViewMode;
  /**
   * False for a `locked` code-backed catalog preview (EAINT-844 Phase F1), which is deliberately
   * previewed WITHOUT sphere context. True (default) for a real Space/composition target — its
   * components resolve sphere-scoped data, so an unpicked anchor sphere renders a composer-owned
   * empty-state (Phase F4c) instead of letting each component fall through to its own generic
   * error card.
   */
  requiresAnchorSphere?: boolean;
  /** Fires when the empty-state's "pick an anchor sphere" action is used. */
  onRequestAnchorSphere?: () => void;
  /**
   * Enables react-grid-layout drag/resize on the draft's `SpaceGrid` (EAINT-844 Phase F5).
   * Absent (or `viewMode !== 'compose'`) leaves the grid locked — Try/Wires mode and read-only
   * targets never receive this.
   */
  onGridPositionChange?: (componentId: string, gridPosition: GridPosition) => void;
  className?: string;
}

const SURFACE_ID = 'composer-canvas';

/**
 * The composer renders metasphere-toolbar chrome as a NON-INTERACTIVE stand-in, never the live
 * `MetaspaceToolbar`. The composer is an authoring surface: its canvas routes every action through
 * `CompositionHost`'s `ctx.onAction` so Try mode can classify and block write-shaped actions
 * (spec SC5). The live toolbar hosts chat/tasks/notifications panels that call the real API
 * clients directly, bypassing that classifier entirely — mounting it would let an author fire real
 * writes at the anchor sphere from inside a sandbox. It also drags a react-router + redux
 * dependency into a design-system organism. So: chrome is previewed, not operated.
 */
const PREVIEW_TOOLBAR_ITEMS: ReadonlyArray<{ id: ToolbarItem; icon: string; labelKey: string }> = [
  { id: 'stream', icon: 'pi pi-align-left', labelKey: 'toolbar.stream' },
  { id: 'notifications', icon: 'pi pi-bell', labelKey: 'toolbar.notifications' },
  { id: 'tasks', icon: 'pi pi-check-square', labelKey: 'toolbar.tasks' },
  { id: 'status', icon: 'pi pi-chart-bar', labelKey: 'toolbar.status' },
  { id: 'chatbox', icon: 'pi pi-comments', labelKey: 'toolbar.chat' },
  { id: 'trajectory', icon: 'pi pi-chart-line', labelKey: 'toolbar.trajectory' },
];

/**
 * The composer's canvas: renders `assembleRenderInput(state, ...)` — draft, then session
 * overrides, then ghost proposals, in that order (SC3/SC4/SC8) — through `CompositionHost` in
 * `preview` mode so interactive adapters still emit actions without ever writing (SC5). Forwards
 * `CompositionHost`'s `inspect` handle so a parent can resolve a node id it already has (e.g. from
 * an agent proposal) without a synthetic click.
 */
const ComposerCanvasOrganism = forwardRef<CompositionHostHandle, ComposerCanvasOrganismProps>(
  (
    {
      state,
      apiClient,
      sphereId,
      onSelect,
      initialSelectedNodeId,
      onAction,
      viewMode = 'compose',
      requiresAnchorSphere = true,
      onRequestAnchorSphere,
      onGridPositionChange,
      className,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { resolveComposition, isResolving } = useCompositionResolver(state.draft, apiClient);
    const gridEditable = viewMode === 'compose' && Boolean(onGridPositionChange);
    const env = useMemo(
      () => ({ sphereId, gridEditable, onGridPositionChange: gridEditable ? onGridPositionChange : undefined }),
      [sphereId, gridEditable, onGridPositionChange]
    );
    const messages = useMemo(() => assembleRenderInput(state, SURFACE_ID), [state]);

    // Shared with the runtime grid so the WYSIWYG preview cannot drift from what actually renders.
    const settings = state.draft.settings;
    const showToolbar = shouldShowMetaspaceToolbar(
      state.draft.a2ui.components.map((entry) => entry.component),
      settings?.showMetasphereToolbar
    );
    // Mirrors the runtime toolbar's precedence: enabled allowlist > disabled denylist > all.
    // Unknown strings in either list are dropped rather than cast into `ToolbarItem`, so a typo
    // in the inspector cannot smuggle an unrenderable item into the preview.
    const enabledToolbarItems = settings?.enabledToolbarItems;
    const disabledToolbarItems = settings?.disabledToolbarItems;
    const previewToolbarItems = useMemo(() => {
      if (enabledToolbarItems && enabledToolbarItems.length > 0) {
        return PREVIEW_TOOLBAR_ITEMS.filter((item) => enabledToolbarItems.includes(item.id));
      }
      if (disabledToolbarItems && disabledToolbarItems.length > 0) {
        return PREVIEW_TOOLBAR_ITEMS.filter((item) => !disabledToolbarItems.includes(item.id));
      }
      return PREVIEW_TOOLBAR_ITEMS;
    }, [enabledToolbarItems, disabledToolbarItems]);
    const containerRef = useRef<HTMLDivElement>(null);
    const [wireLines, setWireLines] = useState<WireOverlayLine[]>([]);
    const wires = state.draft.wires;
    const ghostIds = useMemo(
      () => new Set(state.ghosts.flatMap((ghost) => ghost.components.map((component) => component.id))),
      [state.ghosts]
    );
    const overrideIds = useMemo(() => new Set(Object.keys(state.overrides)), [state.overrides]);

    // Ghosts and session overrides ride in `messages` as ordinary component updates (SC3/SC4/SC8) —
    // `CompositionHost` has no per-node decoration hook, so the ghost/override id sets are matched
    // against the rendered `[data-component-id]` nodes imperatively, same as the wires measurement
    // below (spec: "Ghost styling is applied by the canvas wrapper via `[data-component-id]`
    // selectors from the ghost id set — `CompositionHost` is unchanged").
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;
      container.querySelectorAll<HTMLElement>('[data-component-id]').forEach((el) => {
        const id = el.getAttribute('data-component-id');
        if (id && ghostIds.has(id)) {
          el.setAttribute('data-composer-ghost', 'true');
        } else {
          el.removeAttribute('data-composer-ghost');
        }
        if (id && overrideIds.has(id)) {
          el.setAttribute('data-composer-override', 'true');
        } else {
          el.removeAttribute('data-composer-override');
        }
      });
    }, [messages, ghostIds, overrideIds]);

    // Measured from the live DOM rather than the document's declared layout — the composer never
    // computes its own positions, `SpaceGrid` does, so this is the only source of truth for where a
    // wired component actually renders.
    useEffect(() => {
      if (viewMode !== 'wires' || !containerRef.current) {
        setWireLines((prev) => (prev.length === 0 ? prev : []));
        return undefined;
      }
      const container = containerRef.current;
      const measure = () => {
        const rectsById = new Map<string, DOMRect>();
        container.querySelectorAll<HTMLElement>('[data-component-id]').forEach((el) => {
          const id = el.getAttribute('data-component-id');
          if (id) rectsById.set(id, el.getBoundingClientRect());
        });
        setWireLines(computeWireOverlayLines(wires, rectsById, container.getBoundingClientRect()));
      };
      measure();
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }, [viewMode, wires, messages]);

    if (isResolving) {
      return (
        <div className="composer-canvas composer-canvas--resolving">
          <LoaderMolecule variant="inline" caption={t('composer.canvas.resolving')} />
        </div>
      );
    }

    // A sphere-scoped target with no anchor sphere picked renders its own components' generic
    // error cards otherwise (each one independently failing to resolve context data) — one
    // composer-owned empty-state in their place (EAINT-844 Phase F4c).
    if (requiresAnchorSphere && !sphereId) {
      return (
        <div className="composer-canvas composer-canvas--empty" data-testid="composer-canvas-empty-state">
          <p className="composer-canvas__empty-message">{t('composer.canvas.pickAnchorSphereMessage')}</p>
          {onRequestAnchorSphere && (
            <ButtonAtom variant="primary" size="small" onClick={onRequestAnchorSphere}>
              {t('composer.canvas.pickAnchorSphereAction')}
            </ButtonAtom>
          )}
        </div>
      );
    }

    return (
      <div
        ref={containerRef}
        data-testid="composer-canvas"
        className={[
          'composer-canvas',
          `composer-canvas--${viewMode}`,
          showToolbar && viewMode !== 'wires' ? 'composer-canvas--with-toolbar' : undefined,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {showToolbar && viewMode !== 'wires' && (
          <div
            className="composer-canvas__toolbar composer-canvas__toolbar--preview"
            data-testid="composer-canvas-toolbar"
          >
            <ChipAtom className="composer-canvas__toolbar-preview-badge" label={t('composer.canvas.toolbarPreviewBadge')} />
            <ul className="composer-canvas__toolbar-items" aria-hidden="true">
              {previewToolbarItems.map((item) => (
                <li
                  key={item.id}
                  className="composer-canvas__toolbar-item"
                  data-testid={`composer-canvas-toolbar-item-${item.id}`}
                  title={t(item.labelKey)}
                >
                  <i className={item.icon} />
                  <span className="composer-canvas__toolbar-item-label">{t(item.labelKey)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <CompositionHost
          ref={ref}
          messages={messages}
          surfaceId={SURFACE_ID}
          mode="preview"
          env={env}
          resolveComposition={resolveComposition}
          catalogEntries={SPACE_CATALOG_ENTRIES}
          onAction={onAction}
          onSelect={onSelect}
          initialSelectedComponentId={initialSelectedNodeId}
        />
        {viewMode === 'wires' && wireLines.length > 0 && (
          <svg className="composer-canvas__wires-svg" data-testid="composer-wires-overlay">
            {wireLines.map((line) => (
              <g key={line.wireId}>
                <line x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} className="composer-canvas__wire" />
                <text x={line.labelX} y={line.labelY} className="composer-canvas__wire-label">
                  {line.label}
                </text>
              </g>
            ))}
          </svg>
        )}
      </div>
    );
  }
);

ComposerCanvasOrganism.displayName = 'ComposerCanvasOrganism';

export default ComposerCanvasOrganism;
